import { useState } from 'react';
import styled from 'styled-components';
import { BaseCodeEditor } from '../../CodeEditor/CodeEditor';
import { Flex } from '../../Flex';
import CopyButton from '../components/CopyButton';
import {
  componentToken,
  fontFamily,
  fontSize,
  space,
  declaration,
} from '../../../style/utils';

const COLLAPSE_THRESHOLD = 12;

type Props = {
  value: string;
  language?: string;
};

const Container = styled.div`
  border-radius: ${componentToken('codeEditor.chat.radius')};
  overflow: hidden;
  background: ${componentToken('codeEditor.chat.background')};
  border: ${componentToken('codeEditor.chat.border')};

  && .ace_editor {
    ${({ theme }) =>
      declaration(
        'border',
        theme.tokens.components.codeEditor.chat.editorBorder,
      )}
    ${({ theme }) =>
      declaration(
        'border-radius',
        theme.tokens.components.codeEditor.chat.editorRadius,
      )}
  }
`;

const Header = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  padding: ${space('xxs', 'smMinus', 'xxs', 'md')};
  background: ${componentToken('codeEditor.chat.bar')};
  border-bottom: ${componentToken('codeEditor.chat.barDivider')};
`;

const Meta = styled(Flex)`
  align-items: center;
  gap: ${space('sm')};
  font-size: ${fontSize('sm')};
  color: ${componentToken('codeEditor.chat.meta')};
`;

const LanguageLabel = styled.span`
  font-family: ${fontFamily('mono')};
  text-transform: lowercase;
`;

const EditorScroll = styled.div`
  overflow-x: auto;
`;

const Footer = styled.button`
  display: block;
  width: 100%;
  padding: ${space('smMinus', 'md')};
  border: none;
  border-top: ${componentToken('codeEditor.chat.barDivider')};
  background: ${componentToken('codeEditor.chat.bar')};
  color: ${componentToken('codeEditor.chat.link')};
  font-size: ${fontSize('sm')};
  text-align: left;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`;

const ChatCodeBlock = ({ value, language }: Props) => {
  const [expanded, setExpanded] = useState(false);

  const lines = value.split('\n');
  const lineCount = lines.length;
  const isCollapsible = lineCount > COLLAPSE_THRESHOLD;
  const hiddenLineCount = lineCount - COLLAPSE_THRESHOLD;

  const displayValue =
    isCollapsible && !expanded
      ? lines.slice(0, COLLAPSE_THRESHOLD).join('\n')
      : value;

  return (
    <Container>
      <Header>
        <Meta>
          {language && <LanguageLabel>{language}</LanguageLabel>}
          <span>
            {lineCount} {lineCount === 1 ? 'line' : 'lines'}
          </span>
        </Meta>
        <CopyButton content={value} strokeColor="code.editorIcon" />
      </Header>

      <EditorScroll>
        <BaseCodeEditor
          mode={language || 'text'}
          value={displayValue}
          fontSize={13}
          minLines={1}
          maxLines={Infinity}
          readOnly
          showPrintMargin={false}
          wrapEnabled={false}
        />
      </EditorScroll>

      {isCollapsible && (
        <Footer onClick={() => setExpanded((prev) => !prev)}>
          {expanded
            ? 'Show less'
            : `Show ${hiddenLineCount} more ${
                hiddenLineCount === 1 ? 'line' : 'lines'
              }`}
        </Footer>
      )}
    </Container>
  );
};

export default ChatCodeBlock;
